const currency = new Intl.NumberFormat('ru-RU', {
  style: 'currency',
  currency: 'RUB',
  maximumFractionDigits: 0,
});

const monthFormatter = new Intl.DateTimeFormat('ru-RU', { month: 'short', year: 'numeric' });

const NBSP = ' ';
const MINUS = '−';

/** Full currency string from integer cents: `15 500 000 ₽`. */
export function formatRub(cents: number): string {
  return currency.format(cents / 100);
}

const wholeNumber = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });

/** Whole rubles without the currency sign, for dense table cells: `15 418 292`. */
export function formatRubPlain(cents: number): string {
  return wholeNumber.format(cents / 100);
}

const withKopecks = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Rubles and kopecks without the currency sign, for the payments table: `107 013,70`. */
export function formatRubKopecksPlain(cents: number): string {
  return withKopecks.format(cents / 100);
}

/** Compact amount without the currency sign: `15,5 млн`, `12,5 млн`, `820 тыс.`, `999`, `0`. */
export function formatCompactAmount(cents: number): string {
  const rub = cents / 100;
  const abs = Math.abs(rub);
  const sign = rub < 0 ? MINUS : '';
  const units: Array<[number, string]> = [
    [1e9, 'млрд'],
    [1e6, 'млн'],
    [1e3, 'тыс.'],
  ];
  for (const [divisor, suffix] of units) {
    if (abs >= divisor) {
      const scaled = abs / divisor;
      // One decimal below 100 units so axis ticks such as 12,5 млн survive; 820 тыс. stays whole.
      const digits = scaled < 100 ? 1 : 0;
      const text = scaled.toLocaleString('ru-RU', { maximumFractionDigits: digits });
      return `${sign}${text}${NBSP}${suffix}`;
    }
  }
  return `${sign}${Math.round(abs).toLocaleString('ru-RU')}`;
}

/** Compact currency for axes: `15,5 млн ₽`, `820 тыс. ₽`, `0 ₽`. */
export function formatCompactRub(cents: number): string {
  return `${formatCompactAmount(cents)}${NBSP}₽`;
}

/** Signed currency: `+120 000 ₽`, `−120 000 ₽`, `—` for zero. */
export function formatSignedRub(cents: number): string {
  if (cents === 0) return '—';
  const abs = currency.format(Math.abs(cents) / 100);
  return cents > 0 ? `+${abs}` : `${MINUS}${abs}`;
}

type YearMonth = { year: number; month: number };

/** `2026-01` → `{ year: 2026, month: 1 }`; `null` when the value is not a month. */
function parseMonth(value: string): YearMonth | null {
  const [year, month] = value.split('-').map(Number);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return null;
  return { year, month };
}

/** `2026-01` → `янв. 2026` (short month, no «г.»); returns the input unchanged when it is not a month. */
export function formatMonthLabel(value: string): string {
  const parsed = parseMonth(value);
  if (!parsed) return value;
  return monthFormatter.format(new Date(Date.UTC(parsed.year, parsed.month - 1, 1))).replace(/\s*г\.$/, '');
}

/** Month names in the prepositional case («в январе»). */
const MONTHS_IN = [
  'январе', 'феврале', 'марте', 'апреле', 'мае', 'июне',
  'июле', 'августе', 'сентябре', 'октябре', 'ноябре', 'декабре',
];

/** Month names in the genitive case («с января»). */
const MONTHS_FROM = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

/** Month names in the accusative case («по февраль»; same as the nominative). */
const MONTHS_TO = [
  'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
  'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь',
];

/** `2026-01` → `январе 2026`, for sentences such as «первый платёж в январе 2026». */
export function formatMonthIn(value: string): string {
  const parsed = parseMonth(value);
  if (!parsed) return value;
  return `${MONTHS_IN[parsed.month - 1]} ${parsed.year}`;
}

/** `2026-01` → `января 2026`, for sentences such as «с января 2026 до конца кредита». */
export function formatMonthFrom(value: string): string {
  const parsed = parseMonth(value);
  if (!parsed) return value;
  return `${MONTHS_FROM[parsed.month - 1]} ${parsed.year}`;
}

/**
 * Inclusive month range for a sentence: `с января по февраль 2026`; the first year is
 * repeated only when the range spans two years (`с января 2026 по декабрь 2027`).
 */
export function formatMonthRange(start: string, end: string): string {
  const from = parseMonth(start);
  const to = parseMonth(end);
  if (!from || !to) return `с ${start} по ${end}`;
  const fromText = from.year === to.year ? MONTHS_FROM[from.month - 1] : `${MONTHS_FROM[from.month - 1]} ${from.year}`;
  return `с ${fromText} по ${MONTHS_TO[to.month - 1]} ${to.year}`;
}

function pluralYears(years: number): string {
  const mod10 = years % 10;
  const mod100 = years % 100;
  if (mod10 === 1 && mod100 !== 11) return 'год';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'года';
  return 'лет';
}

/** `86` → `7 лет · 2 мес.`; `12` → `1 год`; `5` → `5 мес.`. */
export function formatTerm(months: number): string {
  const total = Math.max(0, Math.round(months));
  const years = Math.floor(total / 12);
  const rest = total % 12;
  if (years === 0) return `${rest} мес.`;
  const yearsLabel = `${years} ${pluralYears(years)}`;
  return rest ? `${yearsLabel} · ${rest} мес.` : yearsLabel;
}

/** `16.9` → `16,9 %`. */
export function formatPercent(rate: number, maxDigits = 2): string {
  const text = rate.toLocaleString('ru-RU', { maximumFractionDigits: maxDigits });
  return `${text}${NBSP}%`;
}
